// src/components/risk/RiskScoreGauge.jsx
// Circular gauge component displaying client risk score with category indicator

import React from 'react';
import { getRiskCategory } from './constants';

const RiskScoreGauge = ({
  score = 0,
  size = 'md',
  showLabel = true,
  showCategory = true,
  animated = true,
  className = ''
}) => {
  const category = getRiskCategory(score);
  const normalizedScore = Math.min(100, Math.max(0, Number(score) || 0));

  // Size configurations
  const sizes = {
    sm: { outer: 80, stroke: 6, fontSize: 'text-lg', labelSize: 'text-xs' },
    md: { outer: 120, stroke: 8, fontSize: 'text-2xl', labelSize: 'text-sm' },
    lg: { outer: 160, stroke: 10, fontSize: 'text-4xl', labelSize: 'text-base' },
    xl: { outer: 200, stroke: 12, fontSize: 'text-5xl', labelSize: 'text-lg' }
  };

  const config = sizes[size] || sizes.md;
  const radius = (config.outer - config.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (normalizedScore / 100) * circumference;
  const dashOffset = circumference - progress;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width: config.outer, height: config.outer }}>
        {/* Background circle */}
        <svg
          width={config.outer}
          height={config.outer}
          className="transform -rotate-90"
        >
          {/* Track */}
          <circle
            cx={config.outer / 2}
            cy={config.outer / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={config.stroke}
            className="text-slate-700/30"
          />

          {/* Progress arc */}
          <circle
            cx={config.outer / 2}
            cy={config.outer / 2}
            r={radius}
            fill="none"
            stroke={category.color}
            strokeWidth={config.stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={animated ? dashOffset : circumference}
            className={animated ? 'transition-all duration-1000 ease-out' : ''}
            style={animated ? { strokeDashoffset: dashOffset } : {}}
          />
        </svg>

        {/* Center score display */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={`font-bold ${config.fontSize}`}
            style={{ color: category.color }}
          >
            {Math.round(normalizedScore)}
          </span>
          {showLabel && (
            <span className={`text-slate-400 ${config.labelSize}`}>
              Risk Score
            </span>
          )}
        </div>
      </div>

      {/* Category badge */}
      {showCategory && (
        <div
          className="mt-2 px-3 py-1 rounded-full text-white text-sm font-medium"
          style={{ backgroundColor: category.color }}
        >
          {category.name}
        </div>
      )}
    </div>
  );
};

// Mini inline version for tables
export const RiskScoreBadge = ({ score, className = '' }) => {
  const category = getRiskCategory(score);
  const normalizedScore = Math.min(100, Math.max(0, Number(score) || 0));

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
        style={{ backgroundColor: category.color }}
      >
        {Math.round(normalizedScore)}
      </div>
      <span className={`text-xs font-medium ${category.textColor}`}>
        {category.name}
      </span>
    </div>
  );
};

// Horizontal bar version for compact display
export const RiskScoreBar = ({ score, className = '' }) => {
  const category = getRiskCategory(score);
  const normalizedScore = Math.min(100, Math.max(0, Number(score) || 0));

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex-1 h-2 bg-slate-700/30 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${normalizedScore}%`,
            backgroundColor: category.color
          }}
        />
      </div>
      <span
        className="font-bold text-sm min-w-[2rem] text-right"
        style={{ color: category.color }}
      >
        {Math.round(normalizedScore)}
      </span>
    </div>
  );
};

export default RiskScoreGauge;
